import { makeStyles } from '@map/theme';
import { useSnackbar } from '@mui/base/useSnackbar';
import { Typography } from '@mui/material';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import AlertIcon from '@ui/Icon/library/AlertIcon';
import CheckIcon from '@ui/Icon/library/CheckIcon';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import { ReactNode, useEffect, useRef } from 'react';
import { Transition } from 'react-transition-group';

const transitionDuration = 200;
const useStyles = makeStyles(
  (
    { borderRadius, spacing, palette, sizing, shadow, zIndex },
    { variant }: Pick<Props, 'variant'>,
  ) => ({
    text: {
      flexGrow: 1,
      marginRight: spacing(4),
    },
    snackbar: {
      pointerEvents: 'initial',
      gridArea: '1 / 2 / 2 / 3',
      display: 'flex',
      alignItems: 'center',
      minWidth: sizing(68),
      maxWidth: sizing(140),
      backgroundColor: palette[variant][100],
      color: palette[variant][700],
      border: `1px solid ${palette[variant][500]}`,
      borderRadius: borderRadius('m'),
      padding: spacing(4),
      boxShadow: shadow('m'),
      gap: spacing(2),
    },
    container: {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: zIndex.snackbar,
      top: 0,
      left: 0,
      right: 0,
      padding: spacing(6),
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      transition: `opacity ${transitionDuration}ms ease-in-out`,
      '&::before, &::after': {
        height: sizing(5),
        gridArea: '1 / 2 / 2 / 3',
        borderRadius: borderRadius('m'),
        position: 'relative',
        backgroundColor: palette[variant][100],
        color: palette[variant][700],
        border: `1px solid ${palette[variant][500]}`,
      },
      '&::before': {
        zIndex: -1,
        top: spacing(-1),
        margin: spacing(0, 4),
      },
      '&::after': {
        zIndex: -2,
        top: spacing(-2),
        margin: spacing(0, 8),
      },
      '&[data-stack="1"]::before, &[data-stack="2"]::before, &[data-stack="2"]::after': {
        content: '""',
      },
    },
  }),
);

type Props = {
  open: boolean;
  id?: number;
  variant: 'success' | 'error' | 'info' | 'warning';
  message: ReactNode;
  onClose?: () => void;
  autoHideDuration?: number | null;
  stackCount?: number;
};

const ToastNotification = (props: Props) => {
  const { id, open, variant, message, onClose, autoHideDuration, stackCount } = props;
  const { classes } = useStyles({ variant });

  const { getRootProps } = useSnackbar({ onClose, open });
  useEffect(() => {
    if (open && autoHideDuration) {
      const timer = setTimeout(() => {
        onClose?.();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose, id]);
  const nodeRef = useRef(null);

  return (
    <Transition
      timeout={{ enter: transitionDuration, exit: transitionDuration }}
      in={open}
      appear
      unmountOnExit
      nodeRef={nodeRef}
    >
      {(status) => (
        <div
          className={classes.container}
          style={{ opacity: status === 'entered' ? 1 : 0 }}
          data-stack={Math.min(2, stackCount ?? 0)}
          ref={nodeRef}
        >
          <div className={classes.snackbar} {...getRootProps()}>
            <Icon
              variant="filled"
              Component={variant === 'success' ? CheckIcon : AlertIcon}
              color={variant}
            />
            <Typography className={classes.text} variant="body2">
              {message}
            </Typography>
            <Button icon variant="tertiary" onClick={onClose}>
              <CloseIcon color="primary" />
            </Button>
          </div>
        </div>
      )}
    </Transition>
  );
};

export default ToastNotification;
